import React, { useId, useRef, useState } from 'react';
import { useButton } from '@react-aria/button';
import { useFocusRing } from '@react-aria/focus';
import { useHover } from '@react-aria/interactions';
import clsx from 'clsx';

interface CollapsibleSectionProps {
    /** Header label — plain string or any node (icon + text, etc.) */
    title: React.ReactNode;
    /** Shown on the right side of the header, e.g. a count badge */
    badge?: React.ReactNode;
    /** Whether the section starts expanded. Defaults to true. */
    defaultExpanded?: boolean;
    /** Controlled expanded state — omit for uncontrolled usage */
    isExpanded?: boolean;
    /** Called with the new value whenever the toggle is triggered */
    onToggle?: (expanded: boolean) => void;
    children: React.ReactNode;
    className?: string;
    /** Extra classes applied to the animated content wrapper */
    contentClassName?: string;
}

/**
 * A fully-accessible, animated accordion/collapsible section.
 *
 * • Uses React Aria `useButton` for the trigger — keyboard, pointer and
 *   touch all handled correctly.
 * • `aria-expanded` / `aria-controls` pair links trigger ↔ panel.
 * • Animation is driven by the CSS `grid-template-rows: 0fr → 1fr` trick,
 *   which avoids the max-height hack and gives buttery-smooth transitions
 *   without any JS measurement.
 *
 * Can be used controlled or uncontrolled:
 *   // Uncontrolled (internal state)
 *   <CollapsibleSection title="Entries" defaultExpanded={false}>…</CollapsibleSection>
 *
 *   // Controlled
 *   <CollapsibleSection title="Entries" isExpanded={open} onToggle={setOpen}>…</CollapsibleSection>
 */
export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
                                                                          title,
                                                                          badge,
                                                                          defaultExpanded = true,
                                                                          isExpanded: controlledExpanded,
                                                                          onToggle,
                                                                          children,
                                                                          className,
                                                                          contentClassName,
                                                                      }) => {
    const isControlled = controlledExpanded !== undefined;
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
    const expanded = isControlled ? controlledExpanded : internalExpanded;

    const triggerId = useId();
    const panelId = useId();

    const triggerRef = useRef<HTMLButtonElement>(null);

    const handleToggle = () => {
        const next = !expanded;
        if (!isControlled) setInternalExpanded(next);
        onToggle?.(next);
    };

    const { buttonProps } = useButton(
        { onPress: handleToggle, 'aria-expanded': expanded, 'aria-controls': panelId },
        triggerRef
    );
    const { isFocusVisible, focusProps } = useFocusRing();
    const { hoverProps, isHovered } = useHover({});

    return (
        <div className={clsx('flex flex-col', className)}>
            {/* ── Trigger ────────────────────────────────────────────────── */}
            <button
                {...buttonProps}
                {...hoverProps}
                {...focusProps}
                id={triggerId}
                ref={triggerRef}
                className={clsx(
                    'flex w-full items-center justify-between gap-2',
                    'rounded-lg px-1 py-1.5',
                    'text-left text-sm font-medium text-gray-600',
                    'transition-colors duration-150 focus:outline-none',
                    isHovered && 'bg-gray-50 text-gray-800',
                    isFocusVisible && 'ring-2 ring-pink-500 ring-offset-1'
                )}
            >
                <div className="flex items-center gap-2 min-w-0">
                    {/* Chevron */}
                    <svg
                        aria-hidden="true"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={clsx(
                            'h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform duration-300 ease-in-out',
                            expanded && 'rotate-90'
                        )}
                    >
                        <path d="M6 4l4 4-4 4" />
                    </svg>

                    <span className="truncate">{title}</span>
                </div>

                {badge !== undefined && (
                    <span className="shrink-0">{badge}</span>
                )}
            </button>

            {/* ── Animated panel ─────────────────────────────────────────── */}
            {/*
                The outer div transitions `grid-template-rows` between 0fr and 1fr.
                The inner div needs `overflow: hidden` so content is clipped while
                animating, but `min-h-0` ensures the collapsed state actually hides it.
            */}
            <div
                id={panelId}
                role="region"
                aria-labelledby={triggerId}
                style={{
                    display: 'grid',
                    gridTemplateRows: expanded ? '1fr' : '0fr',
                    transition: 'grid-template-rows 280ms cubic-bezier(0.4, 0, 0.2, 1)',
                }}
            >
                <div className={clsx('overflow-hidden', contentClassName)}>
                    {children}
                </div>
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────────────────────────────────────────
   Count badge — small pill useful for "3 Einträge" next to the header title
───────────────────────────────────────────────────────────────────────────── */
interface CountBadgeProps {
    count: number;
    className?: string;
}

export const CountBadge: React.FC<CountBadgeProps> = ({ count, className }) => (
    <span
        className={clsx(
            'inline-flex items-center justify-center',
            'min-w-[1.25rem] rounded-full px-1.5 py-0.5',
            'bg-gray-100 text-xs font-medium text-gray-500',
            className
        )}
    >
        {count}
    </span>
);